/* eslint-disable no-restricted-globals */

import {
  hrtPaymentResultsLayer,
  landingLayer,
  mainSurveyLayer,
  paymentCheckoutLayer,
  paymentSurveyLayer,
} from "@pageDefinitions/goto/constants";
import { captureException } from "src/utils/error";
import goto from ".";
import {
  isInsideAndroidWebView,
  isInsideIOSWebKit,
  pageSetPushReroll,
  setParam,
} from "./utils";
import {
  getAndroidMessageHandlers,
  getIOSMessageHandlers,
} from "src/utils/in-app/messageHandlers";
import {
  getHmRouteId,
  getMeristemContext,
  getRouteId,
} from "@utils/meristemContext";
import plans from "@utils/plansMapping";
import { isHM } from "src/utils/userSegment";
import {
  updateForwardedParams,
  updateURLParams,
  getURLParams,
} from "src/utils/urlParams";
import { reroll } from "../components/PageSetRouter";
import { routeConstants } from "src/utils/constants";
import { getSessionState } from "@pageDefinitions/session";
import { init as clearPiiParams } from "@utils/urlParamsPii";
import { buildDeepLink } from "src/utils/in-app/deeplinkHandler";

export function isBuyflow() {
  return ["landing", "main-survey"].includes(getMeristemContext().context_type);
}

export const linkTo = {
  mainSurvey() {
    return "/ps/main-survey";
  },
};

const routeSwitch = (currentRoute: string, newRoute: string) => {
  if (currentRoute !== newRoute) {
    // Ensure that if we come back, the route is preserved
    updateURLParams({ route: currentRoute });
    updateForwardedParams({ route: newRoute });
  }
};

const hmRouteSwitch = (
  currentRoute: string = getRouteId(),
  newRoute: string = getHmRouteId()
) => {
  routeSwitch(currentRoute, newRoute);
};

export default {
  // Landing
  landing(replaceHistory = false, routeId?: string, entryPoint?: string) {
    goto.layer(landingLayer, null, replaceHistory, routeId, entryPoint);
  },
  hmLanding() {
    hmRouteSwitch();
    reroll(`/programs/mood/${getHmRouteId()}`);
  },
  directHome() {
    clearPiiParams();

    const params = new URLSearchParams(window.location.search);
    setParam(params, "route", routeConstants.direct);

    window.location.href = `/?${params}`;
  },
  // Survey
  async mainSurvey(
    replaceHistory = false,
    routeId?: string,
    entryPoint?: string
  ) {
    return goto.layer(
      mainSurveyLayer,
      null,
      replaceHistory,
      routeId,
      entryPoint
    );
  },

  async switchToMedMainSurvey() {
    updateURLParams({ route: getRouteId() });
    goto.mainSurvey(false, routeConstants.clinical, "continueWithBuildingMed");
  },

  // Survey
  async paymentSurvey(
    replaceHistory = false,
    routeId?: string,
    entryPoint?: string
  ) {
    return goto.layer(
      paymentSurveyLayer,
      null,
      replaceHistory,
      routeId,
      entryPoint
    );
  },

  async hrtPaymentResultsSurvey(replaceHistory = false, routeId?: string) {
    return goto.layer(hrtPaymentResultsLayer, null, replaceHistory, routeId);
  },

  // NW payment checkout
  async paymentCheckout(noomPlanId: string) {
    updateURLParams({
      noom_plan_id: noomPlanId,
    });
    updateForwardedParams({
      noom_plan_id: plans["3-7-DAY-TRIAL"],
    });

    routeSwitch(getRouteId(), routeConstants.direct);
    return goto.layer(paymentCheckoutLayer);
  },

  // NW payment checkout
  async paymentCheckoutInitialRoute(noomPlanId: string) {
    const { initialRouteId } = getSessionState("browser");
    updateURLParams({
      noom_plan_id: noomPlanId,
    });

    routeSwitch(getRouteId(), initialRouteId || routeConstants.direct);
    return goto.layer(paymentCheckoutLayer);
  },

  // HM Survey
  async hmMainSurvey() {
    hmRouteSwitch();
    return goto.mainSurvey();
  },

  async hwDirectMainSurvey(entryPoint?: string) {
    updateURLParams({ route: getRouteId() });
    return goto.mainSurvey(false, routeConstants.direct, entryPoint);
  },

  async hwFreemiumMainSurvey(entryPoint?: string) {
    updateURLParams({ route: getRouteId() });
    return goto.mainSurvey(false, routeConstants.freemiumWeight, entryPoint);
  },

  async hwDirectPaymentSurvey() {
    updateURLParams({ route: getRouteId() });
    return goto.paymentSurvey(false, routeConstants.direct);
  },

  async hwPaymentSurveyInitialRoute(entryPoint?: string) {
    const { initialRouteId } = getSessionState("browser");
    updateURLParams({ route: getRouteId() });
    return goto.paymentSurvey(
      false,
      initialRouteId || routeConstants.direct,
      entryPoint
    );
  },

  async hwFreemiumPaymentSurvey(entryPoint?: string) {
    updateURLParams({ route: getRouteId() });
    return goto.paymentSurvey(false, routeConstants.freemiumWeight, entryPoint);
  },

  /**
   * This is intended mostly as a stopgap and may not work for all future implementations.
   * @deprecated Use page set navigation instead.
   */
  async surveyQuestion(question: string) {
    if (!question) {
      goto.mainSurvey();
      return;
    }

    pageSetPushReroll(
      isHM() ? `/survey/hm/${question}` : `/survey/${question}`
    );
  },

  async choosePlan() {
    pageSetPushReroll(isHM() ? "/survey/hm/choosePlan" : "/survey/choosePlan");
  },

  nativeBuyflowExit() {
    if (isInsideIOSWebKit()) {
      try {
        const messageHandlers = getIOSMessageHandlers();
        messageHandlers?.buyflowExit?.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativeBuyflowExit.ios");
      }
    }
    if (isInsideAndroidWebView()) {
      try {
        const messageHandlers = getAndroidMessageHandlers();
        if (messageHandlers && messageHandlers.buyflowExit) {
          messageHandlers.buyflowExit();
          return true;
        }
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativeBuyflowExit.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativeBuyflowExit: no message handler found");

    // go to the landing page in case native buyflow exit fails
    goto.landing();
    return false;
  },
  nativeWebviewExit() {
    if (isInsideIOSWebKit()) {
      const webviewExit = getIOSMessageHandlers()?.webviewExit;
      webviewExit?.postMessage({});
      return !!webviewExit;
    }
    if (isInsideAndroidWebView()) {
      const webviewExit = getAndroidMessageHandlers()?.webviewExit;
      if (webviewExit) {
        webviewExit();
      }
      return !!webviewExit;
    }
    return false;
  },
  closeInAppContentScreen() {
    // score=1 will mark the in-app task as complete
    window.location.href = "https://unknown.noom.com?score=1&close=1";
  },
  closeInAppWebview() {
    window.location.href = "https://unknown.noom.com?close=1";
  },

  handleFreemiumComplete() {
    let messageHandler;
    if (isInsideAndroidWebView()) {
      messageHandler = getAndroidMessageHandlers();
      messageHandler?.buyflowPurchaseCompleted();
    } else if (isInsideIOSWebKit()) {
      messageHandler = getIOSMessageHandlers();
      messageHandler?.freemiumBuyflowComplete?.postMessage({});
    }
  },

  deeplink(params: {
    open: string;
    close: string;
    forwardParams?: {
      originalParam: string;
      deeplinkParam: string;
    }[];
    extraParams?: object;
  }) {
    const { open, close, forwardParams, extraParams } = params;
    const allParams = { open, close };
    if (forwardParams) {
      const existingParams = getURLParams();
      forwardParams.forEach(({ originalParam, deeplinkParam }) => {
        if (existingParams[originalParam]) {
          // just ignore if the original param does not exist
          allParams[deeplinkParam] = existingParams[originalParam];
        }
      });
    }
    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        allParams[key] = value;
      });
    }
    const useNativeDeepLink = !isInsideAndroidWebView();
    window.location.href = buildDeepLink(allParams, useNativeDeepLink);
  },
};
